import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Textarea } from "@/components/ui/textarea"
import { Typography } from "@/components/ui/typography"
import { useEmailSenderIdentity } from "@/features/material-compliance/api/inquiryEmailApi"
import type { InquiryTemplateSettings } from "@/features/material-compliance/components/supplier-declarations/inquiry/settings/templateSettingsMock"
import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { useOrganization } from "@/services/api/organizationApi"
import { OrgLogoSection } from "../../../components/OrgLogoSection"
import {
  NEEDS_WRITING_FIELD_CLASS,
  PlaceholderNotice,
} from "../../../components/PlaceholderNotice"
import { SettingsSwitchField } from "../../../components/SettingsControls"
import { SettingsSection } from "../../../components/SettingsPage"
import { SettingsWithPreview } from "../../../components/SettingsPreview"
import { TemplateSaveButton } from "../../../components/TemplateSaveButton"
import { TemplateSettingsLoader } from "../../../components/TemplateSettingsLoader"
import { useSavedDraft } from "../../../hooks/useSavedDraft"
import {
  useTemplateSettingsSave,
  type TemplateSettingsSaver,
} from "../../../hooks/useTemplateSettingsSave"
import { AttachmentPreview } from "../../../previews/AttachmentPreview"
import { DeclarationPdfPreview } from "../../../previews/DeclarationPdfPreview"
import { WorkbookPreview } from "../../../previews/WorkbookPreview"
import { needsWriting } from "../../../setup/placeholders"
import {
  pdfFooterNeedsSetup,
  pdfFooterPrints,
} from "../../../setup/setupChecks"

type AttachmentFormat = InquiryTemplateSettings["defaultAttachmentFormat"]

const FORMATS: {
  value: AttachmentFormat
  labelKey: TranslationKeys
  hintKey: TranslationKeys
}[] = [
  {
    value: "pdf",
    labelKey: "MC_INQ_DRAFT_FORMAT_PDF",
    hintKey: "SETTINGS/SUPPLIER_INQUIRIES/FORMAT_PDF_HINT",
  },
  {
    value: "excel",
    labelKey: "MC_INQ_DRAFT_FORMAT_EXCEL",
    hintKey: "SETTINGS/SUPPLIER_INQUIRIES/FORMAT_EXCEL_HINT",
  },
  {
    value: "pdf_and_excel",
    labelKey: "MC_INQ_DRAFT_FORMAT_PDF_EXCEL",
    hintKey: "SETTINGS/SUPPLIER_INQUIRIES/FORMAT_PDF_EXCEL_HINT",
  },
]

const formatLabelKey = (format: AttachmentFormat): TranslationKeys =>
  FORMATS.find(({ value }) => value === format)?.labelKey ??
  "MC_INQ_DRAFT_FORMAT_PDF"

const PREVIEW_NOTE_KEYS: Record<AttachmentFormat, TranslationKeys> = {
  pdf: "SETTINGS/SUPPLIER_INQUIRIES/PREVIEW_PDF",
  excel: "SETTINGS/SUPPLIER_INQUIRIES/PREVIEW_EXCEL",
  pdf_and_excel: "SETTINGS/SUPPLIER_INQUIRIES/PREVIEW_PDF_EXCEL",
}

/**
 * What a new inquiry attaches in the format chosen above, as the supplier
 * gets it: for PDF the declaration page and a question page, each with the
 * PDF footer; for PDF + Excel the declaration PDF, which prints the company
 * name and sending address where the footer would be, beside the workbook;
 * for Excel the workbook alone, without any branding.
 */
function FormatPreview({
  format,
  footer,
  contactPerson,
  signOff,
}: {
  format: AttachmentFormat
  footer: string
  contactPerson: string
  signOff: string
}) {
  const { translate } = useTranslation()
  const { data: org } = useOrganization()
  const { data: identity } = useEmailSenderIdentity()
  const logoUrl = org?.logoUrl ?? null

  return (
    <div className="flex flex-col gap-2">
      {format !== "excel" && (
        <DeclarationPdfPreview
          logoUrl={logoUrl}
          companyName={org?.name ?? ""}
          address={org?.addressMultiline ?? ""}
          contactPerson={contactPerson}
          sendingAddress={identity?.fullAddress ?? null}
          signOff={signOff}
          footer={format === "pdf" ? footer : undefined}
        />
      )}
      {format === "pdf" && (
        <AttachmentPreview logoUrl={logoUrl} footer={footer} />
      )}
      {format === "pdf_and_excel" && <WorkbookPreview compact />}
      {format === "excel" && <WorkbookPreview />}
      <Typography as="muted" className="text-xs">
        {translate(PREVIEW_NOTE_KEYS[format])}
      </Typography>
    </div>
  )
}

/** The PDF footer, with what is still a placeholder in it and whether the
 *  org's format prints it at all. The text is held by the caller: the
 *  Attachment tab previews it as it is typed. */
function PdfFooterSection({
  saved,
  saver,
  footer,
  setFooter,
  footerPrints,
  formatLabel,
}: {
  saved: InquiryTemplateSettings
  saver: TemplateSettingsSaver
  footer: string
  setFooter: (footer: string) => void
  footerPrints: boolean
  formatLabel: string
}) {
  const { translate } = useTranslation()
  const footerTodo = footerPrints && needsWriting(footer)

  return (
    <SettingsSection
      title={translate("MC_TPL_FOOTER_LABEL")}
      target="inquiry.footer"
      info={translate("SETTINGS/INFO/ALSO_RUNNING")}
      description={translate("MC_TPL_FOOTER_HINT")}
      needsSetup={pdfFooterNeedsSetup(saved)}
      footer={
        <TemplateSaveButton
          saver={saver}
          saveKey="footer"
          dirty={footer !== saved.questionnaireFooter}
          patch={() => ({ questionnaireFooter: footer })}
        />
      }
    >
      <Textarea
        aria-label={translate("MC_TPL_FOOTER_LABEL")}
        aria-describedby={footerTodo ? "settings-footer-todo" : undefined}
        rows={3}
        value={footer}
        disabled={!saver.canEdit}
        className={cn(footerTodo && NEEDS_WRITING_FIELD_CLASS)}
        onChange={(event) => setFooter(event.target.value)}
      />
      {footerTodo && (
        <PlaceholderNotice id="settings-footer-todo" text={footer} />
      )}
      {!footerPrints && (
        <Typography as="muted">
          {translate("SETTINGS/SUPPLIER_INQUIRIES/FOOTER_NOT_PRINTED", {
            format: formatLabel,
          })}
        </Typography>
      )}
    </SettingsSection>
  )
}

/** The PDF footer with its own Save, for the inquiry draft's side sheet. */
function PdfFooterForm({ saved }: { saved: InquiryTemplateSettings }) {
  const { translate } = useTranslation()
  const saver = useTemplateSettingsSave()
  const [footer, setFooter] = useSavedDraft(saved.questionnaireFooter)
  return (
    <PdfFooterSection
      saved={saved}
      saver={saver}
      footer={footer}
      setFooter={setFooter}
      footerPrints={pdfFooterPrints(saved)}
      formatLabel={translate(formatLabelKey(saved.defaultAttachmentFormat))}
    />
  )
}

export function PdfFooterSettings() {
  return (
    <TemplateSettingsLoader>
      {(settings) => <PdfFooterForm saved={settings} />}
    </TemplateSettingsLoader>
  )
}

function AttachmentForm({
  saved,
  saver,
}: {
  saved: InquiryTemplateSettings
  saver: TemplateSettingsSaver
}) {
  const { translate, currentLanguage } = useTranslation()
  const [format, setFormat] = useSavedDraft(saved.defaultAttachmentFormat)
  const [locked, setLocked] = useSavedDraft(saved.attachmentFormatLocked)
  const [footer, setFooter] = useSavedDraft(saved.questionnaireFooter)
  const [contactPerson, setContactPerson] = useSavedDraft(saved.contactPerson)
  const readOnly = !saver.canEdit
  // As chosen above, before it is saved: a format that never prints the
  // footer needs no footer written.
  const footerPrints = pdfFooterPrints({
    ...saved,
    defaultAttachmentFormat: format,
    attachmentFormatLocked: locked,
  })
  const formatLabel = translate(formatLabelKey(format))

  return (
    <SettingsWithPreview
      preview={
        <FormatPreview
          format={format}
          footer={footer}
          contactPerson={contactPerson}
          // The signature is set under Email sending, in the language on
          // screen here; an inquiry prints the one in its own language.
          signOff={
            saved.emailSignatures[currentLanguage === "DE" ? "de" : "en"]
          }
        />
      }
    >
      <div className="flex flex-col divide-y">
        <OrgLogoSection shared />

        <SettingsSection
          title={translate("MC_TPL_ATTACHMENT_FORMAT_HEADING")}
          target="inquiry.format"
          // Locked, the default replaces every inquiry's own format, also on
          // the reminders of running ones.
          info={translate(
            locked ? "SETTINGS/INFO/ALL_INQUIRIES" : "SETTINGS/INFO/NEW_ONLY",
          )}
          description={translate("MC_TPL_ATTACHMENT_FORMAT_HINT")}
          footer={
            <TemplateSaveButton
              saver={saver}
              saveKey="format"
              dirty={
                format !== saved.defaultAttachmentFormat ||
                locked !== saved.attachmentFormatLocked
              }
              patch={() => ({
                defaultAttachmentFormat: format,
                attachmentFormatLocked: locked,
              })}
            />
          }
        >
          <RadioGroup
            value={format}
            disabled={readOnly}
            onValueChange={(value) => setFormat(value as AttachmentFormat)}
            aria-label={translate("MC_TPL_ATTACHMENT_FORMAT_HEADING")}
            className="gap-4"
          >
            {FORMATS.map(({ value, labelKey, hintKey }) => (
              <div key={value} className="flex items-start gap-3">
                <RadioGroupItem
                  value={value}
                  id={`settings-format-${value}`}
                  aria-describedby={`settings-format-${value}-hint`}
                />
                <div className="flex flex-col gap-1">
                  <Label htmlFor={`settings-format-${value}`}>
                    {translate(labelKey)}
                  </Label>
                  <Typography as="muted" id={`settings-format-${value}-hint`}>
                    {translate(hintKey)}
                  </Typography>
                </div>
              </div>
            ))}
          </RadioGroup>
          {/* Stored inverted: `attachmentFormatLocked` enforces the default. */}
          <SettingsSwitchField
            id="settings-format-choice"
            label={translate("MC_TPL_ATTACHMENT_FORMAT_CHOICE_LABEL")}
            description={translate("MC_TPL_ATTACHMENT_FORMAT_CHOICE_HINT")}
            checked={!locked}
            disabled={readOnly}
            onCheckedChange={(allowed) => setLocked(!allowed)}
          />
        </SettingsSection>

        <PdfFooterSection
          saved={saved}
          saver={saver}
          footer={footer}
          setFooter={setFooter}
          footerPrints={footerPrints}
          formatLabel={formatLabel}
        />

        <SettingsSection
          title={translate("MC_TPL_CONTACT_LABEL")}
          target="inquiry.contactPerson"
          info={translate("SETTINGS/INFO/ALSO_RUNNING")}
          description={translate("MC_TPL_CONTACT_HINT")}
          footer={
            <TemplateSaveButton
              saver={saver}
              saveKey="contact-person"
              dirty={contactPerson !== saved.contactPerson}
              patch={() => ({ contactPerson })}
            />
          }
        >
          <Input
            aria-label={translate("MC_TPL_CONTACT_LABEL")}
            value={contactPerson}
            disabled={readOnly}
            onChange={(event) => setContactPerson(event.target.value)}
          />
        </SettingsSection>
      </div>
    </SettingsWithPreview>
  )
}

/** What a supplier inquiry attaches: the file format, and the logo, footer
 *  and contact person printed on the questionnaire. */
export function AttachmentTab({ saver }: { saver: TemplateSettingsSaver }) {
  return (
    <TemplateSettingsLoader>
      {(settings) => <AttachmentForm saved={settings} saver={saver} />}
    </TemplateSettingsLoader>
  )
}
